import {
  primeOrdersCache,
  clearOrdersCache,
  dropOrderHistoryOverlays,
  fetchOrdersForBillIds,
  fetchOrdersForBusinessDay,
  fetchOrdersForMonth,
  getHistoryOrders,
  getOrdersWithHistory,
  setHistoryOrders,
  subscribedOrdersLiveStartIso,
} from './orders'
import {
  primeBillsCache,
  clearBillsCache,
  dropBillHistoryOverlays,
  fetchBillsForBusinessDay,
  fetchBillsForMonth,
  getBillsWithHistory,
  getHistoryBills,
  setHistoryBills,
} from './bills'
import { primeTablesCache, clearTablesCache } from './tables'
import { primeTableAreasCache, clearTableAreasCache } from './tableAreas'
import { primeFloorSpacesCache, clearFloorSpacesCache } from './floorSpaces'
import { primeFloorPlacementsCache, clearFloorPlacementsCache } from './floorPlacements'
import { primeFloorObjectsCache, clearFloorObjectsCache } from './floorObjects'
import { primeQrCodesCache, clearQrCodesCache } from './qrCodes'
import { primeCategoriesCache, clearCategoriesCache } from './categories'
import { primeStationsCache, clearStationsCache } from './stations'
import { primeMenuItemsCache, clearMenuItemsCache } from './menuItems'
import { primeSettingsCache, clearSettingsCache } from './settings'
import {
  primeItemsCache,
  clearItemsCache,
  dropItemHistoryOverlays,
  fetchItemsForOrderIds,
  getHistoryItems,
  mergeItemsIntoHistory,
  setHistoryItems,
} from './items'
import { liveStartIso, nowMs } from './liveCacheWindow'
import { recordReadyMs } from './perfPulse'

let historyOverlayLabel: string | null = null
let historyOverlayEpoch = 0

export function getHistoryOverlayMeta(): {
  label: string | null
  orders: number
  bills: number
  items: number
} {
  return {
    label: historyOverlayLabel,
    orders: getHistoryOrders().length,
    bills: getHistoryBills().length,
    items: getHistoryItems().length,
  }
}

/** Prime all Firestore caches. Call once when app loads with Firestore enabled. */
export async function primeAllCaches(): Promise<void> {
  const started = nowMs()
  await primeCategoriesCache()
  await primeTablesCache()
  await primeBillsCache()
  await primeOrdersCache()
  await primeItemsCache()
  await Promise.all([
    primeTableAreasCache(),
    primeFloorSpacesCache(),
    primeFloorPlacementsCache(),
    primeFloorObjectsCache(),
    primeQrCodesCache(),
    primeStationsCache(),
    primeMenuItemsCache(),
    primeSettingsCache(),
  ])
  recordReadyMs(nowMs() - started)
}

/** Clear all in-memory Firestore caches. Call on logout so the next user does not see previous account/place data. */
export function clearAllCaches(): void {
  dropHistoryOverlays()
  clearOrdersCache()
  clearBillsCache()
  clearItemsCache()
  clearTablesCache()
  clearTableAreasCache()
  clearFloorSpacesCache()
  clearFloorPlacementsCache()
  clearFloorObjectsCache()
  clearQrCodesCache()
  clearCategoriesCache()
  clearStationsCache()
  clearMenuItemsCache()
  clearSettingsCache()
}

export function dropHistoryOverlays(): void {
  historyOverlayEpoch += 1
  historyOverlayLabel = null
  dropOrderHistoryOverlays()
  dropBillHistoryOverlays()
  dropItemHistoryOverlays()
}

export async function resubscribeLiveCachesForBusinessDay(): Promise<void> {
  const nextStart = liveStartIso()
  if (nextStart === subscribedOrdersLiveStartIso()) return
  dropHistoryOverlays()
  await primeBillsCache()
  await primeOrdersCache()
  await primeItemsCache()
}

async function applyHistoryOverlay(
  label: string,
  epoch: number,
  bills: Awaited<ReturnType<typeof fetchBillsForBusinessDay>>,
  orders: Awaited<ReturnType<typeof fetchOrdersForBusinessDay>>,
): Promise<void> {
  if (epoch !== historyOverlayEpoch) return
  const orderIds = [
    ...new Set([
      ...orders.map((o) => o.id),
      ...bills.flatMap((b) => b.orderIds ?? []),
    ]),
  ]
  const items = await fetchItemsForOrderIds(orderIds)
  if (epoch !== historyOverlayEpoch) return
  historyOverlayLabel = label
  setHistoryBills(bills)
  setHistoryOrders(orders)
  setHistoryItems(items)
}

/** Fetch order docs (and items) for live-day bills whose orderIds are not in cache. */
export async function ensureLiveDayMissingBillOrders(): Promise<void> {
  const bills = getBillsWithHistory()
  const have = new Set(getOrdersWithHistory().map((o) => o.id))
  const billsNeeding = bills.filter((b) => (b.orderIds ?? []).some((id) => !have.has(id)))
  if (billsNeeding.length === 0) return
  const extra = await fetchOrdersForBillIds(billsNeeding.map((b) => b.id))
  const newOrders = extra.filter((o) => !have.has(o.id))
  if (newOrders.length === 0) return
  setHistoryOrders([...getHistoryOrders(), ...newOrders])
  const items = await fetchItemsForOrderIds(newOrders.map((o) => o.id))
  await mergeItemsIntoHistory(items)
}

export async function ensureHistoryForBusinessDay(date: string): Promise<void> {
  dropHistoryOverlays()
  const epoch = historyOverlayEpoch
  historyOverlayLabel = date
  const bills = await fetchBillsForBusinessDay(date)
  const orders = await fetchOrdersForBusinessDay(date)
  await applyHistoryOverlay(date, epoch, bills, orders)
}

export async function ensureHistoryForMonth(monthKey: string): Promise<void> {
  dropHistoryOverlays()
  const epoch = historyOverlayEpoch
  historyOverlayLabel = monthKey
  const bills = await fetchBillsForMonth(monthKey)
  const orders = await fetchOrdersForMonth(monthKey)
  await applyHistoryOverlay(monthKey, epoch, bills, orders)
}
